import { PayloadAction } from '@reduxjs/toolkit';

import { CapabilityKeys } from '@hcs/types';
import { createHcReduxSlice } from '@hcs/utils';

import {
  DexpEndpoint,
  DexpEndpointsFilterIds,
  DexpEndpointsParams,
  DexpJobPropertyInput,
} from '../types';

export type DexpEndpointInCart = {
  capabilityKey: CapabilityKeys;
  endpointId: string;
  selected: boolean;
};
export type DexpEndpointsInCart = DexpEndpointInCart[];

export interface DataExplorerSliceState {
  filters: DexpEndpointsParams;
  endpointsInCart: DexpEndpointsInCart;
  propertiesInCart?:
    | (DexpJobPropertyInput & { selected: boolean })[]
    | undefined;
  newAnalysisStatus: null | 'progress' | 'error' | 'success';
  newAnalysisJobId: number | null;
  newAnalysisError: Error | null;
}

export interface DataExplorerPartialAppState {
  dataExplorerSlice: DataExplorerSliceState;
}

export const DEXP_SLICE_INITIAL_STATE: DataExplorerSliceState = {
  filters: {},
  endpointsInCart: [],
  propertiesInCart: undefined,
  newAnalysisStatus: null,
  newAnalysisError: null,
  newAnalysisJobId: null,
};

export const dataExplorerSlice = createHcReduxSlice({
  name: 'dataExplorerSlice',
  initialState: DEXP_SLICE_INITIAL_STATE,
  reducers: {
    // Endpoint Table
    endpointTableChangeFilter: (
      state,
      action: PayloadAction<{ filterChanges: DexpEndpointsParams }>,
    ) => {
      let field: DexpEndpointsFilterIds;
      for (field in action.payload.filterChanges) {
        const filterValue = action.payload.filterChanges[field];
        state.filters = {
          ...state.filters,
          [field]: filterValue ? filterValue : undefined,
        };
      }
    },
    // New Analysis State - Endpoint Actions
    endpointAddToCart(
      state,
      action: PayloadAction<{
        endpoint: DexpEndpoint;
      }>,
    ) {
      state.endpointsInCart = [
        ...state.endpointsInCart.filter(
          (e) => e.endpointId !== action.payload.endpoint.id,
        ),
        {
          capabilityKey: action.payload.endpoint.capabilityKey,
          endpointId: action.payload.endpoint.id,
          selected: true,
        },
      ];
    },
    endpointRemoveFromCart(
      state,
      action: PayloadAction<{ endpointId: string }>,
    ) {
      state.endpointsInCart = state.endpointsInCart.filter(
        (e) => e.endpointId !== action.payload.endpointId,
      );
    },
    endpointToggleSelect(state, action: PayloadAction<{ endpointId: string }>) {
      state.endpointsInCart = state.endpointsInCart.map((e) =>
        e.endpointId === action.payload.endpointId
          ? { ...e, selected: !e.selected }
          : e,
      );
    },
    endpointClearCart(state) {
      state.endpointsInCart = [];
    },
    // New Analysis State - Property Actions
    setPropertiesInCart(
      state,
      action: PayloadAction<{ properties: DexpJobPropertyInput[] }>,
    ) {
      state.propertiesInCart = action.payload.properties.map((p) => ({
        ...p,
        selected: true,
      }));
    },
    propertyAddToCart(
      state,
      action: PayloadAction<{ property: DexpJobPropertyInput }>,
    ) {
      state.propertiesInCart = [
        ...(state.propertiesInCart || []).filter(
          (p) => p.hcAddressId !== action.payload.property.hcAddressId,
        ),
        { ...action.payload.property, selected: true },
      ];
    },
    propertyRemoveFromCart(
      state,
      action: PayloadAction<{
        hcAddressId: number;
      }>,
    ) {
      state.propertiesInCart = state.propertiesInCart?.filter(
        (p) => p.hcAddressId !== action.payload.hcAddressId,
      );
    },
    propertyToggleSelect(
      state,
      action: PayloadAction<{
        hcAddressId: number;
      }>,
    ) {
      state.propertiesInCart = state.propertiesInCart?.map((p) =>
        p.hcAddressId === action.payload.hcAddressId
          ? { ...p, selected: !p.selected }
          : p,
      );
    },
    propertyClearCart(state) {
      state.propertiesInCart = [];
    },
    propertySetClientFileId(
      state,
      action: PayloadAction<{
        property: DexpJobPropertyInput;
      }>,
    ) {
      state.propertiesInCart = state.propertiesInCart?.map((p) =>
        p.hcAddressId === action.payload.property.hcAddressId
          ? { ...p, clientFileId: action.payload.property.clientFileId }
          : p,
      );
    },
    setNewAnalysisStatus(
      state,
      action: PayloadAction<{
        status: DataExplorerSliceState['newAnalysisStatus'];
      }>,
    ) {
      state.newAnalysisStatus = action.payload.status;
    },
    setNewAnalysisJobId(
      state,
      action: PayloadAction<{
        jobId: DataExplorerSliceState['newAnalysisJobId'];
      }>,
    ) {
      state.newAnalysisJobId = action.payload.jobId;
      if (action.payload.jobId === null) {
        state.newAnalysisStatus = null;
      }
    },
    setNewAnalysisError(
      state,
      action: PayloadAction<{
        error: DataExplorerSliceState['newAnalysisError'];
      }>,
    ) {
      state.newAnalysisError = action.payload.error;
    },
    resetAnalysisState(state) {
      state.endpointsInCart = DEXP_SLICE_INITIAL_STATE.endpointsInCart;
      state.propertiesInCart = DEXP_SLICE_INITIAL_STATE.propertiesInCart;
      state.newAnalysisStatus = DEXP_SLICE_INITIAL_STATE.newAnalysisStatus;
      state.newAnalysisError = DEXP_SLICE_INITIAL_STATE.newAnalysisError;
      state.newAnalysisJobId = DEXP_SLICE_INITIAL_STATE.newAnalysisJobId;
    },
  },
});
